import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

/**
 * Standard nickname editor. Follows the app-wide modal template:
 *  - Shadcn <Dialog> shell (not window.prompt or window.confirm)
 *  - clear title, one-line helper description, single input, primary + secondary actions
 *  - Escape / click-outside dismiss
 *  - Enter submits, Esc cancels
 *
 * Rule (see /app/design_guidelines.md): every user-facing prompt/confirm must
 * use this Dialog template — never window.prompt or window.confirm.
 */
export default function NicknameEditDialog({
  open, onClose, initialValue, title, description, maxLength = 50, onSave,
}) {
  const [value, setValue] = useState(initialValue || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setValue(initialValue || "");
  }, [open, initialValue]);

  const trimmed = value.trim();
  const tooLong = trimmed.length > maxLength;

  const submit = async () => {
    if (tooLong || busy) return;
    setBusy(true);
    try {
      await onSave(trimmed || null);
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="nickname-dialog">
        <DialogHeader>
          <DialogTitle data-testid="nickname-dialog-title">{title}</DialogTitle>
          <DialogDescription data-testid="nickname-dialog-desc">
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); submit(); }
              if (e.key === "Escape") onClose?.();
            }}
            placeholder="e.g. Personal Paper"
            maxLength={maxLength + 20}
            data-testid="nickname-dialog-input"
          />
          <div className={`text-[10px] mono flex justify-between ${tooLong ? "text-red-400" : "text-muted-foreground"}`}>
            <span>Leave blank to clear.</span>
            <span data-testid="nickname-dialog-counter">{trimmed.length}/{maxLength}</span>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose}
                    data-testid="nickname-dialog-cancel">Cancel</Button>
          <Button type="button" onClick={submit}
                    disabled={tooLong || busy}
                    data-testid="nickname-dialog-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
